import React, { FC, useMemo } from "react";
import {
    AvatarGroup,
    type AvatarGroupSize,
} from "@design-system/Avatar/AvatarGroup";
import { HStack } from "@design-system/HStack/HStack";
import { WORKSPACE_ROLES_LABELS } from "@common/constants";
import { InviteSchema, MemberSchema } from "@common/schemas/client.schema";
import { PendingInviteIcon } from "./PendingInviteIcon";
import { TeamMemberIcon } from "./TeamMemberIcon";

interface ClientTeamListProps {
    members: MemberSchema[];
    invites?: InviteSchema[];
    appendContent?: React.ReactNode;
    iconSize?: AvatarGroupSize;
    hideRole?: boolean;
}

type ClientTeamListItem =
    | (MemberSchema & { type: "member" })
    | (InviteSchema & { type: "invite" });

const getKey = (item: ClientTeamListItem) =>
    item.type === "member" ? `user-${item.userId}` : `invite-${item.id}`;

const getLabel = (item: ClientTeamListItem) =>
    item.type === "member"
        ? `${item.preferredName ?? item.email} (${WORKSPACE_ROLES_LABELS[item.role]})`
        : `Pending: ${item.email} (${WORKSPACE_ROLES_LABELS[item.role]})`;

export const ClientTeamList: FC<ClientTeamListProps> = ({
    members,
    invites,
    appendContent,
    iconSize = "xs",
    hideRole = false,
}) => {
    const items = useMemo(
        () => [
            ...members.map((member): ClientTeamListItem => ({
                ...member,
                type: "member",
            })),
            ...(invites ?? []).map((invite): ClientTeamListItem => ({
                ...invite,
                type: "invite",
            })),
        ],
        [members, invites],
    );

    return (
        <HStack align="center">
            <AvatarGroup
                items={items}
                getKey={getKey}
                getLabel={getLabel}
                renderAvatar={(item) =>
                    item.type === "member" ? (
                        <TeamMemberIcon
                            name={item.preferredName ?? item.email}
                            role={hideRole ? undefined : item.role}
                            email={item.email}
                            iconColor={item.iconColor}
                            avatar={item.avatar}
                        />
                    ) : (
                        <PendingInviteIcon invite={item} />
                    )
                }
                max={3}
                size={iconSize}
            />
            {appendContent}
        </HStack>
    );
};
